import React, { useEffect, useState } from "react";
import { Form } from "semantic-ui-react";
import { useFormik } from "formik";
import { Competence, Subject, Grade, Level } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { initialValues, validationSchema } from "./CompetenceForm.form";
import { withEmptyOption } from "../../../../utils";

const competenceController = new Competence();
const subjectController = new Subject();
const gradeController = new Grade();
const levelController = new Level();

export function CompetenceForm(props) {
  const {
    onClose,
    onReload,
    competence,
    defaultSubjectId,
    defaultLevelId,
    defaultGradeId,
  } = props;
  const { accessToken } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [levels, setLevels] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loadingGrades, setLoadingGrades] = useState(false);

  // Load subjects
  useEffect(() => {
    (async () => {
      try {
        const response = await subjectController.getSubjects(accessToken);
        const subjectOptions = response.map((subject) => ({
          key: subject.id,
          value: subject.id,
          text: subject.name,
        }));
        setSubjects(subjectOptions);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [accessToken]);

  // Load levels
  useEffect(() => {
    (async () => {
      try {
        const response = await levelController.getLevels(accessToken);
        const levelOptions = response.map((level) => ({
          key: level.id,
          value: level.id,
          text: level.name,
        }));
        setLevels(levelOptions);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [accessToken]);

  const formik = useFormik({
    initialValues: initialValues({
      ...competence,
      subjectId: competence?.subjectId || defaultSubjectId,
      levelId: competence?.grade?.levelId || defaultLevelId,
      gradeId: competence?.gradeId || defaultGradeId,
    }),
    validationSchema: validationSchema(),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        const data = {
          code: formValue.code.toUpperCase(),
          name: formValue.name,
          description: formValue.description,
          subjectId: formValue.subjectId,
          gradeId: formValue.gradeId,
          order: formValue.order,
        };

        if (competence) {
          await competenceController.updateCompetence(
            accessToken,
            competence.id,
            data,
          );
        } else {
          await competenceController.createCompetence(accessToken, data);
        }
        onReload();
        onClose();
      } catch (error) {
        console.error(error);
      }
    },
  });

  // Load grades filtered by the selected level
  useEffect(() => {
    (async () => {
      if (!formik.values.levelId) {
        setGrades([]);
        return;
      }
      setLoadingGrades(true);
      try {
        const response = await gradeController.getGradesByLevelId(
          formik.values.levelId,
          accessToken,
        );
        const gradeOptions = response.map((grade) => ({
          key: grade.id,
          value: grade.id,
          text: grade.name,
        }));
        setGrades(gradeOptions);
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingGrades(false);
      }
    })();
  }, [formik.values.levelId, accessToken]);

  return (
    <Form onSubmit={formik.handleSubmit}>
      <Form.Group widths="equal">
        <Form.Input
          name="code"
          label="Código"
          placeholder="Ej: MAT-C1"
          onChange={formik.handleChange}
          value={formik.values.code}
          error={formik.errors.code}
        />
        <Form.Input
          name="order"
          label="Orden"
          type="number"
          min={1}
          onChange={formik.handleChange}
          value={formik.values.order}
          error={formik.errors.order}
        />
      </Form.Group>
      <Form.Input
        name="name"
        label="Nombre de la competencia"
        placeholder="Ej: Resuelve problemas de cantidad"
        onChange={formik.handleChange}
        value={formik.values.name}
        error={formik.errors.name}
      />
      <Form.Group widths="equal">
        <Form.Select
          className="edunova-select"
          name="subjectId"
          label="Asignatura"
          placeholder="Selecciona asignatura"
          clearable
          options={withEmptyOption(subjects, "Selecciona asignatura")}
          onChange={(_, data) => formik.setFieldValue("subjectId", data.value)}
          value={formik.values.subjectId}
          error={formik.errors.subjectId}
          disabled={!!competence}
        />
        <Form.Select
          className="edunova-select"
          name="levelId"
          label="Nivel"
          placeholder="Selecciona nivel"
          clearable
          options={withEmptyOption(levels, "Selecciona nivel")}
          onChange={(_, data) => {
            formik.setFieldValue("levelId", data.value);
            formik.setFieldValue("gradeId", "");
          }}
          value={formik.values.levelId}
          error={formik.errors.levelId}
          disabled={!!competence}
        />
        <Form.Select
          className="edunova-select"
          name="gradeId"
          label="Grado"
          placeholder="Selecciona grado"
          clearable
          loading={loadingGrades}
          options={withEmptyOption(grades, "Selecciona grado")}
          onChange={(_, data) => formik.setFieldValue("gradeId", data.value)}
          value={formik.values.gradeId}
          error={formik.errors.gradeId}
          disabled={!!competence || !formik.values.levelId}
        />
      </Form.Group>
      <Form.TextArea
        name="description"
        label="Descripción"
        placeholder="Descripción detallada de la competencia..."
        onChange={formik.handleChange}
        value={formik.values.description}
        error={formik.errors.description}
        rows={3}
      />
      <Form.Button type="submit" primary fluid loading={formik.isSubmitting}>
        {competence ? "Actualizar competencia" : "Crear competencia"}
      </Form.Button>
    </Form>
  );
}
